import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { AppShell } from './components/layout/AppShell'
import { PageLoader } from './components/layout/PageLoader'
import { RouteErrorBoundary } from './components/layout/RouteErrorBoundary'

/**
 * A redirect that keeps the query string, and says which tab the old path meant.
 *
 * `<Navigate to="/repository">` discards the search. That mattered for exactly one caller and it was the
 * important one: the Deploy tab's "Merge / resolve conflicts in portal →" link goes to
 * `/pull-requests?pr=<n>`, so the operator arrived at a branches list instead of the pull request they had
 * just opened — in the middle of the land-then-merge flow, with nothing explaining where their PR went.
 */
function KeepQuery({ to, tab }: { to: string; tab?: string }): JSX.Element {
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  if (tab !== undefined && !params.has('tab')) params.set('tab', tab)
  const qs = params.toString()
  return <Navigate to={`${to}${qs ? `?${qs}` : ''}${hash}`} replace />
}

// ── Lazy page imports ─────────────────────────────────────────────────────────

const Dashboard    = lazy(() => import('./pages/Overview'))   // Overview is the Dashboard
const Pipeline     = lazy(() => import('./pages/Pipeline'))
const Delivery     = lazy(() => import('./pages/Delivery'))
const RetrieveDeploy = lazy(() => import('./pages/RetrieveDeploy'))
const Activity     = lazy(() => import('./pages/Activity'))
const Repository   = lazy(() => import('./pages/Repository'))
const Connections  = lazy(() => import('./pages/Connections'))
const Settings     = lazy(() => import('./pages/Settings'))
const RunDetail    = lazy(() => import('./pages/RunDetail'))
const DeploymentErrors = lazy(() => import('./pages/DeploymentErrors'))
const RunLog       = lazy(() => import('./pages/RunLog'))

// Off-nav (still reachable; folded into a host surface in a later phase)
const Compare      = lazy(() => import('./pages/Compare'))            // → R&D mode (P4)
const Deployments  = lazy(() => import('./pages/Deployments'))        // legacy Matrix; History → Activity
const UiGallery    = lazy(() => import('./pages/UiGallery'))

const page = (el: JSX.Element): JSX.Element => <Suspense fallback={<PageLoader />}>{el}</Suspense>

// ── Router ────────────────────────────────────────────────────────────────────

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      // A pathless layout route carrying the error boundary, so a route error
      // (incl. a stale-chunk dynamic-import failure during a rebuild/deploy)
      // renders INSIDE the AppShell outlet — the nav stays put — and stale chunks
      // auto-reload to the fresh build instead of dropping to a raw stack.
      {
        errorElement: <RouteErrorBoundary />,
        children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // ── Primary nav (Workspace · Monitor · Admin) ──────────────────────────
      { path: 'dashboard',       element: page(<Dashboard />) },
      { path: 'pipeline',        element: page(<Pipeline />) },
      { path: 'delivery',        element: page(<Delivery />) },
      { path: 'retrieve-deploy', element: page(<RetrieveDeploy />) },
      { path: 'activity',        element: page(<Activity />) },
      { path: 'repository',      element: page(<Repository />) },
      { path: 'connections',     element: page(<Connections />) },
      { path: 'settings',        element: page(<Settings />) },

      // ── Detail / off-nav surfaces ──────────────────────────────────────────
      { path: 'deployments/runs/:id', element: page(<RunDetail />) },
      { path: 'deployments/runs/:id/errors', element: page(<DeploymentErrors />) },
      { path: 'deployments/runs/:id/logs', element: page(<RunLog />) },
      { path: 'compare',         element: page(<Compare />) },             // folds into R&D (P4)
      { path: 'deployments',     element: page(<Deployments />) },         // legacy Matrix (off-nav)
      { path: 'ui-gallery',      element: page(<UiGallery />) },

      // ── Back-compat redirects for the folded destinations ──────────────────
      //
      // These CARRY THE QUERY STRING and name the tab, because one of them is a link the product itself
      // hands the operator. After opening a PR from the Deploy tab, "Merge / resolve conflicts in portal →"
      // navigates to `/pull-requests?pr=160` — and a bare `<Navigate to="/repository">` drops the search,
      // landing them on the Branches tab with no idea which PR they came for. The link that exists to take
      // you to your pull request could not take you to your pull request.
      { path: 'overview',      element: <Navigate to="/dashboard" replace /> },
      { path: 'branches',      element: <KeepQuery to="/repository" tab="branches" /> },
      { path: 'commits',       element: <KeepQuery to="/repository" tab="commits" /> },
      { path: 'pull-requests', element: <KeepQuery to="/repository" tab="prs" /> },
      { path: 'releases',      element: <KeepQuery to="/repository" tab="releases" /> },
      { path: 'globalkey',     element: <Navigate to="/settings" replace /> },
      { path: 'cml',           element: <Navigate to="/retrieve-deploy" replace /> },   // folded into R&D (Metadata tab)
      { path: 'diff',          element: <Navigate to="/compare" replace /> },
        ],
      },
    ],
  },
])
